<template>
  <div>
    <div
      class="header__wrapper w-full bg-body-primary z-130 top-0 fixed"
      :class="{
        'menu-shadow border-b border-divider-200': !isModalActive,
        'menu-transparent bg-transparent': isTransparent,
        'emojile-background': (currentTab === 'emojile' || routeName === 'emojile') && !isModalActive,
        'bilpop-background': (currentTab === 'bilpop' || routeName === 'bilpop') && !isModalActive,
      }"
    >
      <slot name="headerBefore"></slot>
      <header class="header header--mobile mx-auto flex flex-col">
        <div class="brand font-bold h-13 u-center w-full relative">
          <div
            v-if="showMobileMenuWithLatency"
            class="u-center absolute h-full px-3.5 z-130"
            :class="{ 'right-2': isModalActive, 'left-0': !isModalActive }"
          >
            <OButton
              id="mobile-menu"
              pure
              class="menu-trigger btn btn--hamburger mr-auto text-black text-24 outline-none focus:outline-none"
              :class="{ active: isModalActive }"
              @click="toggleMenu('menu')"
            >
              <span :style="menuStyle"></span>
              <span :style="menuStyle"></span>
              <span :style="menuStyle"></span>
            </OButton>
          </div>
          <!--menu back button-->
          <div v-if="showBackButton" class="u-center absolute z-130 left-0">
            <OButton pure class="h-full w-10 u-center outline-none focus:outline-none pt-2.5" @click="handleMenuBack">
              <OIcon icon="chevron-left-black" />
            </OButton>
          </div>
          <OneDioLogo :style="textStyle" :dark="textStyle.color === 'black'" />

          <div v-if="!isModalActive" class="u-center absolute right-0 h-full px-3.5">
            <OButton v-if="!isLoggedIn" pure class="u-center rounded-full w-full overflow-hidden" @click="handleLoginModal">
              <OImage loading="lazy" src="/images/avatar.svg" alt="giriş yap" class="w-8 h-8" />
            </OButton>
            <div v-else class="flex items-center">
              <OLink :href="`/${NOTIFICATIONS_PATH}`" pure class="relative u-center w-10 h-10">
                <IconNotification class="text-black" :style="textStyle" width="24px" height="23.54px" />
                <div v-if="notificationCount > 0" class="absolute -bottom-1 rounded-full w-1.5 h-1.5 bg-o-red"></div>
              </OLink>
              <OButton pure class="relative w-10 h-10 u-center" @click="toggleMenu('user')">
                <OAvatar rounded :src="avatarUrl" :alt="user.username" class="w-7 h-7" fallback-class="text-xs"></OAvatar>
                <div v-if="conversationsCount" class="absolute -bottom-1 rounded-full w-1.5 h-1.5 bg-o-red"></div>
              </OButton>
            </div>
          </div>
        </div>
        <ModalMenuMobile v-if="isBot" v-show="showMobileMenu" :inline-style="menuInlineStyle" />
      </header>
    </div>
    <div v-if="showBadges || showNews" class="pb-0 pt-13 flex flex-col">
      <div v-if="showNews" class="bg-black-90 w-full h-12 flex items-center mb-1">
        <BreakingNews v-if="IS_ONEDIO" />
        <BreakingNewsOld v-else class="px-2" />
      </div>
      <div v-if="showBadges" class="flex justify-evenly items-center badgeArea mt-0.5">
        <MenuBadgeItem class="u-center w-full" />
      </div>
    </div>
    <div v-else class="pt-13" :style="[$slots.headerBefore ? { paddingTop: '102px' } : {}]" />
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, ref, useRoute } from '@nuxtjs/composition-api';
import { useDevice } from '@/composables/useDevice';
import OneDioLogo from './OneDioLogo.vue';
import MenuBadgeItem from '~/modules/badges/components/MenuBadgeItem';
import IconNotification from '~/static/images/notification.svg?inline';
import { useModal } from '~/composables/useModal';
import { useAuth } from '~/modules/auth/state/useAuth';
import { getImageUrl } from '~/modules/common/utils/imageUtils';
import { OButton, OAvatar, OLink, OImage, OIcon } from '~/modules/common/components/ui';
import { installNotificationContext, useNotification } from '~/modules/notifications/state/useNotificationState';
import { installConversationContext, useConversation } from '~/modules/conversations/state/useConversationState';
import { installCategoryContext, useCategory } from '~/modules/category/state/useCategoryState';
import { useTracking } from '~/modules/tracking/composables/useTracking';
import BreakingNews from '~/modules/common/components/app/header/BreakingNews';
import BreakingNewsOld from '~/modules/common/components/app/header/BreakingNewsOld';
import categoryObject from '~/constants/categories.json';
import { NOTIFICATIONS_PATH, IS_ONEDIO } from '~/constants/config';

export default defineComponent({
  components: {
    OButton,
    BreakingNews,
    BreakingNewsOld,
    OAvatar,
    OneDioLogo,
    MenuBadgeItem,
    IconNotification,
    OLink,
    OImage,
    OIcon,
    ModalMenuMobile: () => import(/* webpackChunkName: "ModalMenuMobile" */ './ModalMenuMobile'),
  },
  setup(props, { slots }) {
    const { isBot } = useDevice();
    const { user, isLoggedIn } = useAuth();
    const { openModal, closeModal, openLoginModal } = useModal();
    installCategoryContext();
    const { menuSteps, deleteMenuStep, clearMenuSteps } = useCategory();
    const { sendMenuImpressionEvent } = useTracking();
    const route = useRoute();
    const badgeShownList = ['haber-slug', 'index', 'dev'];
    const newsShownList = [
      'haber-slug',
      'dev',
      'index',
      'profil-username',
      'profil-username-index-bildirimler',
      'profil-username-index-yorumlar',
      'profil-username-index-favoriler',
      'profil-username-index-icerikler',
      'profil-username-ayarlar',
      'tags',
    ];
    const categories = categoryObject.categories;
    const headerCategories = categoryObject.headerTree;
    const isMenuImpressionEventSent = ref(false);
    const routeName = route.value.name?.split('___')[0]; // i18n prefix remove
    const currentTab = route.value.params.slug;
    const showBadges = computed(() => IS_ONEDIO && badgeShownList.includes(routeName));
    const showNews = computed(() => newsShownList.includes(routeName) || categories[routeName]);
    const isModalActive = ref(false);
    const showMobileMenu = ref(false);
    const menuInlineStyle = ref(slots.headerBefore ? { paddingTop: '102px' } : {});
    const modalType = ref('');
    installNotificationContext();
    const { getNotificationCount, notificationCount } = useNotification();
    installConversationContext();
    const { getConversationsCount, conversationsCount } = useConversation();

    const showMobileMenuWithLatency = ref(isBot.value);
    onMounted(async () => {
      if (isLoggedIn.value) {
        await getNotificationCount();
        await getConversationsCount();
      }
      setTimeout(() => {
        showMobileMenuWithLatency.value = true;
      }, 2000);
      unlockScroll();
    });
    const handleFixKesfetMenu = () => {
      const kesfetMenu = document.getElementById('kesfet-menu');
      if (kesfetMenu) {
        kesfetMenu.style.display = isModalActive.value ? 'none' : 'flex';
      }
    };
    const toggleMenu = async (type) => {
      if (isModalActive.value) {
        if (modalType.value === 'user') {
          const ModalUserMenu = await import('./ModalUserMenu');
          closeModal(ModalUserMenu.default);
        } else {
          showMobileMenu.value = false;
          const ModalMenuMobile = await import('./ModalMenuMobile');
          closeModal(ModalMenuMobile.default);
          unlockScroll();
          clearMenuSteps();
        }
        isModalActive.value = false;
      } else {
        if (type === 'user' || modalType.value === 'user') {
          openModal(
            import('./ModalUserMenu' /* webpackChunkName: "ModalUserMenu" */),
            slots.headerBefore ? { inlineStyle: { paddingTop: '102px' } } : {}
          );
        } else {
          if (!isBot.value) {
            openModal(
              import('./ModalMenuMobile' /* webpackChunkName: "ModalMenuMobile" */),
              slots.headerBefore ? { inlineStyle: { paddingTop: '102px' } } : { inlineStyle: {} }
            );
          }
          lockScroll();
          showMobileMenu.value = true;
          // send menu impression event only once
          if (!isMenuImpressionEventSent.value) {
            isMenuImpressionEventSent.value = true;
            sendMenuImpressionEvent('mobile', { currentCategory: { name: 'Main Menu' }, subCategories: headerCategories });
          }
        }
        isModalActive.value = true;
      }
      modalType.value = type;
      handleFixKesfetMenu();
    };

    const handleMenuBack = () => {
      deleteMenuStep();
    };

    const showBackButton = computed(() => menuSteps.value.length > 0 && isModalActive.value);

    const avatarUrl = computed(() => getImageUrl(user.value?.avatar, { standart: true }));
    const handleLoginModal = async () => {
      await openLoginModal();
    };

    const unlockScroll = () => document.getElementsByTagName('body')[0].classList.remove('no-scroll');
    const lockScroll = () => document.getElementsByTagName('body')[0].classList.add('no-scroll');

    const isTransparent = computed(() => routeName === 'emojile' || routeName === 'bilpop');
    const menuStyle = computed(() => {
      return {
        backgroundColor:
          (isTransparent.value && !isModalActive.value) || ((currentTab === 'emojile' || currentTab === 'bilpop') && !isModalActive.value)
            ? 'white'
            : 'black',
      };
    });
    const textStyle = computed(() => {
      return {
        color:
          (isTransparent.value && !isModalActive.value) || ((currentTab === 'emojile' || currentTab === 'bilpop') && !isModalActive.value)
            ? 'white'
            : 'black',
      };
    });
    return {
      showMobileMenu,
      showMobileMenuWithLatency,
      isBot,
      menuInlineStyle,
      toggleMenu,
      handleMenuBack,
      showBackButton,
      isModalActive,
      NOTIFICATIONS_PATH,
      isLoggedIn,
      handleLoginModal,
      avatarUrl,
      user,
      notificationCount,
      conversationsCount,
      showBadges,
      showNews,
      isTransparent,
      menuStyle,
      textStyle,
      routeName,
      currentTab,
      IS_ONEDIO,
    };
  },
});
</script>
<style scoped>
/* TODO: Will be removed after #e6e6e6 added to tailwind config*/

.header__wrapper {
  transition: transform 1s;
}

.badgeArea {
  border-bottom: 1px solid #e6e6e6;
}

.menu-shadow {
  box-shadow: 1px 1px 10px 0 rgb(139 139 139 / 50%);
}

.menu-transparent {
  box-shadow: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.menu-trigger,
.menu-trigger span {
  display: inline-block;
  transition: all 0.4s;
  box-sizing: border-box;
}

.menu-trigger {
  position: relative;
  width: 24px;
  height: 17px;
  background: none;
  border: none;
  appearance: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.menu-trigger span {
  position: absolute;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: #000;
  border-radius: 3px;
}

.menu-trigger span:nth-of-type(1) {
  top: 0;
}

.menu-trigger span:nth-of-type(2) {
  top: 7px;
}

.menu-trigger span:nth-of-type(3) {
  bottom: 0;
}

#mobile-menu.active span:nth-of-type(1) {
  transform: translateY(7px) rotate(-315deg);
}

#mobile-menu.active span:nth-of-type(2) {
  opacity: 0;
}

#mobile-menu.active span:nth-of-type(3) {
  transform: translateY(-7px) rotate(315deg);
}

.emojile-background {
  background: linear-gradient(166deg, #00084d 53.4%, #2c4389 106.87%);
  border-color: rgba(255, 255, 255, 0.15);
  box-shadow: none;
}

.bilpop-background {
  background: linear-gradient(166deg, #400677 53.4%, #6e2fbf 106.87%);
  border-color: rgba(255, 255, 255, 0.15);
  box-shadow: none;
}
</style>
